import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaSlidersH, FaCog, FaChartArea } from 'react-icons/fa';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import PageHeader from '../../components/PageHeader';
import Btn from '../../components/ui/Btn';
import Modal from '../../components/ui/Modal';
import ConfirmModal from '../../components/ui/ConfirmModal';
import StatusTag from '../../components/ui/StatusTag';
import Field, { TextInput, NativeSelect } from '../../components/ui/Field';
import { assistantUsageService } from '../../services/assistantUsageService';
import { planService } from '../../services/planService';
import { friendlyError } from '../../services/agentsService';
import { levelOf, pctOf, reasonText, summarize, matches, usd } from '../../utils/assistantUsage';
import { toastSuccess, toastError } from '../../utils/toast';

const FILTERS = [
  { key: 'all', label: 'Todos' },
  { key: 'stopped', label: 'Con límite alcanzado' },
  { key: 'near', label: 'Cerca del límite' },
  { key: 'noplan', label: 'Sin límite' },
];
const num = (v) => (v === '' || v == null ? null : Number(v));
const isoDay = (d) => d.toISOString().slice(0, 10);

/** Uso del asistente de smooth por cliente (proyecto): cuánto usa, cuánto cuesta y dónde se le limita. */
export default function AdminAsistentePage() {
  const [rows, setRows] = useState([]);
  const [plans, setPlans] = useState([]);
  const [state, setState] = useState('loading');   // loading | ready | error
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null);
  const [resolving, setResolving] = useState(null);
  const [series, setSeries] = useState(null);
  const [settings, setSettings] = useState(false);
  const [confirmAll, setConfirmAll] = useState(false);

  const load = useCallback(async () => {
    setState((s) => (s === 'ready' ? s : 'loading'));
    try {
      const [data, planList] = await Promise.all([assistantUsageService.overview(), planService.getAll(true)]);
      setRows(data || []); setPlans(planList || []); setState('ready');
    } catch (e) {
      setError(/assistant_admin_overview|schema cache|does not exist/i.test(e.message || '') ? 'Falta correr el script 50 en la base de datos.' : friendlyError(e));
      setState('error');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const sum = useMemo(() => summarize(rows), [rows]);
  const shown = useMemo(() => rows.filter((p) => matches(p, filter, q))
    .sort((a, b) => (Number(b.pending_request ? 1 : 0) - Number(a.pending_request ? 1 : 0)) || (b.pct - a.pct) || String(a.name).localeCompare(String(b.name))), [rows, filter, q]);
  const requests = rows.filter((p) => p.pending_request);
  const warnOnly = rows.filter((p) => p.has_plan && !p.enforce && !p.blocked).length;

  return (
    <div className="max-w-6xl mx-auto">
      <PageHeader title="Uso del asistente" subtitle="Cuánto usa cada cliente el asistente de smooth por chat y por voz, cuánto cuesta y cuándo se le limita. Cuenta el mes en curso, hora de México.">
        <Btn variant="quiet" icon={FaCog} onClick={() => setSettings(true)}>Ajustes</Btn>
      </PageHeader>

      {state === 'error' ? (
        <div role="alert" className="rounded-3xl border border-signal/30 bg-signal-soft p-6 text-sm text-fg">
          <p className="font-semibold">No se pudo cargar el uso.</p><p className="mt-1 text-fg-dim">{error}</p>
          <Btn size="sm" className="mt-3" onClick={load}>Reintentar</Btn>
        </div>
      ) : state === 'loading' ? (
        <div className="space-y-3" aria-busy="true" aria-label="Cargando uso">{[0, 1, 2, 3].map((i) => <div key={i} className="h-20 rounded-2xl skeleton opacity-50" />)}</div>
      ) : (
        <>
          <dl className="grid grid-cols-2 lg:grid-cols-4 rounded-3xl bg-panel ring-1 ring-line divide-x divide-y lg:divide-y-0 divide-line overflow-hidden" style={{ boxShadow: 'var(--shadow)' }}>
            <Stat label="Costo del mes" value={usd(sum.cost)} hint="Lo que le cuesta a Cantera (chat + voz)" />
            <Stat label="Con límite alcanzado" value={sum.stopped} hint="Ya no pueden usarlo hasta que lo amplíes" tone={sum.stopped ? 'signal' : null} />
            <Stat label="Cerca del límite" value={sum.near} hint="Con el aviso encendido" tone={sum.near ? 'caution' : null} />
            <Stat label="Piden más" value={sum.requests} hint="Solicitudes por revisar" tone={sum.requests ? 'caution' : null} />
          </dl>

          {warnOnly > 0 && (
            <section aria-label="Clientes en solo avisar" className="mt-6 flex flex-col gap-3 rounded-3xl bg-raised ring-1 ring-line p-5 sm:flex-row sm:items-center">
              <div className="flex-1">
                <h2 className="font-display text-[17px] font-bold text-fg">{warnOnly} {warnOnly === 1 ? 'cliente está' : 'clientes están'} en «solo avisar»</h2>
                <p className="mt-0.5 text-[13.5px] text-fg-dim">Se les mide y se les avisa, pero el asistente no se detiene al llegar a su límite. Actívalo cuando ya hayas revisado los topes de cada plan con el uso real.</p>
              </div>
              <Btn variant="primary" onClick={() => setConfirmAll(true)}>Activar el corte en todos</Btn>
            </section>
          )}

          {requests.length > 0 && (
            <section aria-label="Solicitudes por revisar" className="mt-6 rounded-3xl border border-caution/30 bg-caution-soft p-5">
              <h2 className="font-display text-[17px] font-bold text-fg">Piden ampliar su límite</h2>
              <ul className="mt-3 divide-y divide-line/60">
                {requests.map((p) => (
                  <li key={p.project_id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-fg truncate">{p.name} <span className="font-normal text-fg-dim">(ID {p.project_id})</span></p>
                      <p className="text-[13px] text-fg-dim">{p.pending_request.message || 'Sin mensaje.'} · {new Date(p.pending_request.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}</p>
                    </div>
                    <Btn size="sm" variant="primary" onClick={() => setResolving(p)}>Revisar</Btn>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <div className="mt-8 flex flex-wrap items-center gap-2" role="group" aria-label="Filtrar clientes">
            {FILTERS.map((f) => (
              <button key={f.key} type="button" onClick={() => setFilter(f.key)} aria-pressed={filter === f.key}
                className={`h-9 px-4 rounded-full text-[13.5px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live
                  ${filter === f.key ? 'bg-fg text-ink' : 'bg-raised text-fg-dim ring-1 ring-inset ring-line hover:text-fg'}`}>{f.label}</button>
            ))}
            <div className="ml-auto w-full sm:w-64"><TextInput value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar cliente o plan" aria-label="Buscar cliente o plan" /></div>
          </div>

          {shown.length === 0 ? (
            <div className="mt-4 rounded-3xl border border-dashed border-fg/15 px-6 py-12 text-center">
              <p className="font-display text-[19px] font-bold text-fg">Ningún cliente coincide</p>
              <p className="mt-1 text-sm text-fg-dim">Cambia el filtro o la búsqueda.</p>
            </div>
          ) : (
            <ul className="mt-4 rounded-3xl bg-panel ring-1 ring-line divide-y divide-line overflow-hidden" style={{ boxShadow: 'var(--shadow)' }}>
              {shown.map((p) => <ProjectRow key={p.project_id} p={p} onEdit={() => setEditing(p)} onSeries={() => setSeries(p)} />)}
            </ul>
          )}
        </>
      )}

      <EditModal project={editing} plans={plans} onClose={() => setEditing(null)} onDone={load} />
      <ResolveModal project={resolving} onClose={() => setResolving(null)} onDone={load} />
      <SeriesModal project={series} onClose={() => setSeries(null)} />
      <SettingsModal open={settings} plans={plans} onClose={() => setSettings(false)} onDone={load} />
      <ConfirmModal open={confirmAll} onClose={() => setConfirmAll(false)} title="Activar el corte en todos los clientes" confirmLabel="Activar el corte"
        onConfirm={async () => { try { const n = await assistantUsageService.enforceAll(true); toastSuccess(n === 0 ? 'Ya estaban todos con corte' : `Corte activado en ${n} ${n === 1 ? 'cliente' : 'clientes'}`); await load(); } catch (e) { toastError(friendlyError(e)); throw e; } }}>
        <p>Desde ahora, cada cliente que llegue al tope de su plan deja de poder usar lo que se pasó (mensajes, voz o todo si es el tope en USD) hasta que empiece el mes o le amplíes el límite. Los clientes nuevos también nacerán con corte.</p>
        <p className="mt-2 text-fg-dim">Los clientes que ya estén por encima de su tope se detienen de inmediato.</p>
      </ConfirmModal>
    </div>
  );
}

function Stat({ label, value, hint, tone }) {
  const color = tone === 'signal' ? 'text-signal' : tone === 'caution' ? 'text-caution' : 'text-fg';
  return (
    <div className="px-5 py-4">
      <dt className="text-[12.5px] font-medium text-fg-dim">{label}</dt>
      <dd className={`mt-1 font-display text-[28px] leading-none font-bold tabular-nums ${color}`}>{value}</dd>
      <p className="mt-1.5 text-[12px] text-fg-faint">{hint}</p>
    </div>
  );
}

function Meter({ label, used, limit, format = (v) => v }) {
  const pct = pctOf(used, limit);
  const over = pct != null && pct >= 100;
  const near = pct != null && pct >= 80;
  return (
    <div className="min-w-[130px]">
      <p className="flex items-baseline justify-between text-[12px] text-fg-dim"><span>{label}</span><span className="tabular-nums text-fg">{format(used)}{limit != null ? <span className="text-fg-faint"> / {format(limit)}</span> : <span className="text-fg-faint"> · sin tope</span>}</span></p>
      <div className="mt-1.5 h-1.5 rounded-full bg-raised overflow-hidden" role={pct != null ? 'progressbar' : undefined} aria-valuenow={pct ?? undefined} aria-valuemin={0} aria-valuemax={100} aria-label={`${label}: ${pct ?? 0} por ciento`}>
        {pct != null && <div className={`h-full rounded-full ${over ? 'bg-signal' : near ? 'bg-caution' : 'bg-live'}`} style={{ width: `${pct}%` }} />}
      </div>
    </div>
  );
}

function ProjectRow({ p, onEdit, onSeries }) {
  const lv = levelOf(p);
  return (
    <li className="px-5 py-4">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
        <div className="min-w-0 lg:w-64 shrink-0">
          <p className="flex flex-wrap items-center gap-2 font-semibold text-fg"><span className="truncate">{p.name}</span><StatusTag tone={lv.tone} dot>{lv.label}</StatusTag></p>
          <p className="text-[13px] text-fg-dim truncate">ID {p.project_id} · {p.plan_name || 'Sin plan'}{!p.enforce && p.has_plan ? ' · solo avisa' : ''}</p>
          {(p.level === 'limit' || p.level === 'blocked' || p.level === 'over') && <p className="text-[12.5px] text-fg-faint">{reasonText(p)}</p>}
        </div>
        <div className="grid flex-1 grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-3">
          <Meter label="Mensajes" used={p.text.used} limit={p.text.limit} />
          <Meter label="Voz (min)" used={p.voice.used_minutes} limit={p.voice.limit_minutes} />
          <Meter label="Costo" used={p.cost.used} limit={p.cost.limit} format={usd} />
        </div>
        <div className="flex gap-2 shrink-0">
          <Btn size="sm" variant="ghost" icon={FaChartArea} onClick={onSeries} aria-label={`Ver el uso diario de ${p.name}`}>Ver uso</Btn>
          <Btn size="sm" variant="quiet" icon={FaSlidersH} onClick={onEdit} aria-label={`Ajustar el plan de ${p.name}`}>Ajustar</Btn>
        </div>
      </div>
    </li>
  );
}

function EditModal({ project: p, plans, onClose, onDone }) {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!p) return;
    setF({
      planId: p.plan_id || '', textTurns: p.custom?.text_turns ?? '', voiceMinutes: p.custom?.voice_minutes ?? '', costCap: p.custom?.cost_cap_usd ?? '',
      blocked: !!p.blocked, reason: p.block_reason || '', enforce: p.enforce !== false,
    });
    setError('');
  }, [p]);
  if (!p || !f) return <Modal open={false} onClose={onClose} title="" />;
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const plan = plans.find((x) => x.id === f.planId);
  const hint = (v, suffix = '') => (v != null ? `El plan da ${v}${suffix}` : 'El plan no pone tope');

  const save = async () => {
    for (const [k, label] of [['textTurns', 'Mensajes'], ['voiceMinutes', 'Minutos de voz'], ['costCap', 'Tope en USD']]) {
      if (f[k] !== '' && (!(Number(f[k]) >= 0) || Number.isNaN(Number(f[k])))) { setError(`${label}: escribe un número igual o mayor que 0, o déjalo vacío.`); return; }
    }
    setBusy(true); setError('');
    try {
      await assistantUsageService.setPlan(p.project_id, { planId: f.planId, textTurns: num(f.textTurns), voiceMinutes: num(f.voiceMinutes), costCap: num(f.costCap), blocked: f.blocked, reason: f.reason, enforce: f.enforce });
      toastSuccess('Plan del asistente guardado'); await onDone(); onClose();
    } catch (e) { setError(friendlyError(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={busy ? undefined : onClose} title={`Asistente de ${p.name}`} description="El plan marca los topes del mes. Si escribes un tope propio, manda sobre el del plan."
      footer={<><Btn variant="ghost" onClick={onClose} disabled={busy}>Cancelar</Btn><Btn variant="primary" onClick={save} loading={busy}>Guardar</Btn></>} width="max-w-xl">
      <div className="space-y-4">
        <Field label="Plan" hint={f.planId ? 'Cambiar de plan no borra el uso del mes.' : 'Sin plan, el cliente no tiene límite; solo se mide su uso.'}>
          <NativeSelect value={f.planId} onChange={set('planId')} data-autofocus>
            <option value="">Sin plan (sin límite)</option>
            {plans.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </NativeSelect>
        </Field>
        <fieldset className="rounded-2xl ring-1 ring-line p-4">
          <legend className="px-1 text-[13px] font-semibold text-fg">Topes de este cliente (vacío = los del plan)</legend>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Mensajes al mes" hint={hint(plan?.assistant_text_turns_monthly)}><TextInput inputMode="numeric" value={f.textTurns} onChange={set('textTurns')} placeholder="Del plan" /></Field>
            <Field label="Minutos de voz" hint={hint(plan?.assistant_voice_minutes_monthly)}><TextInput inputMode="numeric" value={f.voiceMinutes} onChange={set('voiceMinutes')} placeholder="Del plan" /></Field>
            <Field label="Tope en USD" hint={hint(plan?.assistant_cost_cap_usd_monthly != null ? usd(plan.assistant_cost_cap_usd_monthly) : null)}><TextInput inputMode="decimal" value={f.costCap} onChange={set('costCap')} placeholder="Del plan" /></Field>
          </div>
        </fieldset>
        <label className="flex items-start gap-3 text-[14px] text-fg cursor-pointer">
          <input type="checkbox" checked={f.enforce} onChange={set('enforce')} className="mt-1 h-4 w-4 accent-[rgb(var(--live))]" />
          <span><span className="font-semibold">Cortar al llegar al límite</span><br /><span className="text-fg-dim text-[13px]">Si lo apagas, el cliente sigue usando el asistente y tú solo ves que se pasó.</span></span>
        </label>
        <div className="rounded-2xl ring-1 ring-line p-4 space-y-3">
          <label className="flex items-start gap-3 text-[14px] text-fg cursor-pointer">
            <input type="checkbox" checked={f.blocked} onChange={set('blocked')} className="mt-1 h-4 w-4 accent-[rgb(var(--signal))]" />
            <span><span className="font-semibold">Pausar el asistente de este cliente</span><br /><span className="text-fg-dim text-[13px]">Deja de funcionar por chat y voz hasta que lo reactives.</span></span>
          </label>
          {f.blocked && <Field label="Motivo (lo ve el cliente)"><TextInput value={f.reason} onChange={set('reason')} maxLength={160} placeholder="Ej. Pago pendiente" /></Field>}
        </div>
        {error && <p role="alert" className="text-sm text-signal">{error}</p>}
      </div>
    </Modal>
  );
}

function ResolveModal({ project: p, onClose, onDone }) {
  const [extra, setExtra] = useState({ turns: '', minutes: '', usd: '' });
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { if (p) { setExtra({ turns: '', minutes: '', usd: '' }); setNote(''); setError(''); } }, [p]);
  if (!p) return <Modal open={false} onClose={onClose} title="" />;
  const r = p.pending_request;
  const go = async (approve) => {
    if (approve && !(Number(extra.turns) > 0 || Number(extra.minutes) > 0 || Number(extra.usd) > 0)) { setError('Escribe cuánto agregas: mensajes, minutos o dólares.'); return; }
    setBusy(true); setError('');
    try {
      await assistantUsageService.resolve(r.id, approve, { extraTurns: Number(extra.turns) || 0, extraMinutes: Number(extra.minutes) || 0, extraUsd: Number(extra.usd) || 0, note });
      toastSuccess(approve ? 'Solicitud autorizada' : 'Solicitud negada'); await onDone(); onClose();
    } catch (e) { setError(friendlyError(e)); toastError(friendlyError(e)); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={busy ? undefined : onClose} title={`${p.name} pide más`} description={r.message || 'No dejó mensaje.'}
      footer={<><Btn variant="ghost" onClick={onClose} disabled={busy}>Cerrar</Btn><Btn variant="danger" onClick={() => go(false)} disabled={busy}>Negar</Btn><Btn variant="primary" onClick={() => go(true)} loading={busy}>Autorizar</Btn></>} width="max-w-xl">
      <p className="text-[13.5px] text-fg-dim">Va en {p.text.used} mensajes{p.text.limit != null ? ` de ${p.text.limit}` : ''}, {p.voice.used_minutes} min de voz{p.voice.limit_minutes != null ? ` de ${p.voice.limit_minutes}` : ''} y {usd(p.cost.used)}{p.cost.limit != null ? ` de ${usd(p.cost.limit)}` : ''}.</p>
      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        <Field label="Mensajes de más"><TextInput inputMode="numeric" value={extra.turns} onChange={(e) => setExtra((s) => ({ ...s, turns: e.target.value }))} placeholder="0" data-autofocus /></Field>
        <Field label="Minutos de voz de más"><TextInput inputMode="numeric" value={extra.minutes} onChange={(e) => setExtra((s) => ({ ...s, minutes: e.target.value }))} placeholder="0" /></Field>
        <Field label="USD de más"><TextInput inputMode="decimal" value={extra.usd} onChange={(e) => setExtra((s) => ({ ...s, usd: e.target.value }))} placeholder="0" /></Field>
      </div>
      <p className="mt-2 text-[12.5px] text-fg-faint">Se suma al tope de este cliente; no cambia el plan. Si quieres dejarlo permanente, súbele el plan después.</p>
      <div className="mt-4"><Field label="Nota interna" optional><TextInput value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} /></Field></div>
      {error && <p role="alert" className="mt-3 text-sm text-signal">{error}</p>}
    </Modal>
  );
}

function SeriesModal({ project: p, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!p) return;
    setData(null); setError('');
    const to = new Date(); const from = new Date(); from.setDate(from.getDate() - 29);
    assistantUsageService.series(p.project_id, isoDay(from), isoDay(to)).then(setData).catch((e) => setError(friendlyError(e)));
  }, [p]);
  if (!p) return <Modal open={false} onClose={onClose} title="" />;
  const total = (data || []).reduce((a, d) => ({ turns: a.turns + d.text_turns, min: a.min + Number(d.voice_minutes), cost: a.cost + Number(d.cost_usd) }), { turns: 0, min: 0, cost: 0 });
  return (
    <Modal open onClose={onClose} title={`Uso diario de ${p.name}`} description="Últimos 30 días, hora de México." footer={<Btn variant="primary" onClick={onClose}>Cerrar</Btn>} width="max-w-2xl">
      {error ? <p role="alert" className="text-sm text-signal">{error}</p> : !data ? <div className="h-56 rounded-2xl skeleton opacity-50" aria-busy="true" /> : data.length === 0 ? (
        <p className="py-10 text-center text-sm text-fg-dim">Este cliente todavía no usa el asistente en este periodo.</p>
      ) : (
        <>
          <p className="text-[13.5px] text-fg-dim">En total: <strong className="text-fg">{total.turns}</strong> mensajes, <strong className="text-fg">{total.min.toFixed(1)}</strong> min de voz y <strong className="text-fg">{usd(total.cost)}</strong>.</p>
          <div className="mt-3 h-56" role="img" aria-label="Costo diario del asistente en dólares">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                <defs><linearGradient id="asCost" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="rgb(var(--live))" stopOpacity={0.35} /><stop offset="100%" stopColor="rgb(var(--live))" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid stroke="rgb(var(--fg) / 0.08)" vertical={false} />
                <XAxis dataKey="date" tickFormatter={(d) => new Date(`${d}T00:00:00`).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' })} tick={{ fontSize: 11, fill: 'rgb(var(--fg-dim))' }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={(v) => `$${v}`} tick={{ fontSize: 11, fill: 'rgb(var(--fg-dim))' }} axisLine={false} tickLine={false} width={48} />
                <Tooltip formatter={(v, n) => (n === 'cost_usd' ? [usd(v), 'Costo'] : [v, n])} labelFormatter={(d) => new Date(`${d}T00:00:00`).toLocaleDateString('es-MX', { day: 'numeric', month: 'long' })}
                  contentStyle={{ background: 'rgb(var(--raised))', border: '1px solid rgb(var(--fg) / 0.08)', borderRadius: 12, fontSize: 12 }} />
                <Area type="monotone" dataKey="cost_usd" stroke="rgb(var(--live))" strokeWidth={2} fill="url(#asCost)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </Modal>
  );
}

function SettingsModal({ open, plans, onClose, onDone }) {
  const [f, setF] = useState({ vpm: '', warn: '', max: '', defPlan: '', enforce: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!open) return;
    setError('');
    assistantUsageService.settings().then((s) => setF({ vpm: String(s.voice_usd_per_minute), warn: String(s.warn_pct), max: String(Math.round(s.voice_max_session_seconds / 60)), defPlan: s.default_plan_id || '', enforce: !!s.default_enforce })).catch((e) => setError(friendlyError(e)));
  }, [open]);
  const save = async () => {
    const vpm = Number(f.vpm), warn = Number(f.warn), max = Number(f.max);
    if (!(vpm >= 0) || !(warn >= 1 && warn <= 99) || !(max >= 1 && max <= 120)) { setError('Revisa los valores: costo igual o mayor que 0, aviso entre 1 y 99, sesión entre 1 y 120 minutos.'); return; }
    setBusy(true); setError('');
    try {
      await assistantUsageService.settings({ voiceUsdPerMinute: vpm, warnPct: Math.round(warn), voiceMaxSeconds: Math.round(max * 60) });
      await assistantUsageService.defaults(f.defPlan || null, null).catch(() => {});   // sin el script 51 no existe: se ignora
      toastSuccess('Ajustes guardados'); await onDone?.(); onClose();
    }
    catch (e) { setError(friendlyError(e)); } finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={busy ? undefined : onClose} title="Ajustes del asistente" description="Valen para todos los clientes."
      footer={<><Btn variant="ghost" onClick={onClose} disabled={busy}>Cancelar</Btn><Btn variant="primary" onClick={save} loading={busy}>Guardar</Btn></>}>
      <div className="space-y-4">
        <Field label="Plan de los clientes nuevos" hint="Cada cliente que se cree entra con este plan (y en «solo avisar» o con corte, según lo último que elegiste).">
          <NativeSelect value={f.defPlan} onChange={(e) => setF((s) => ({ ...s, defPlan: e.target.value }))}>
            <option value="">Sin plan (sin límite)</option>
            {(plans || []).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </NativeSelect>
        </Field>
        <Field label="Costo mínimo por minuto de voz (USD)" hint="Se cobra al menos esto por cada minuto hablado, aunque la sesión no reporte tokens. Ajústalo con lo que veas en la factura de Google.">
          <TextInput inputMode="decimal" value={f.vpm} onChange={(e) => setF((s) => ({ ...s, vpm: e.target.value }))} data-autofocus />
        </Field>
        <Field label="Avisar cuando lleven (%)" hint="A este porcentaje de su tope el cliente ve que se acerca al límite.">
          <TextInput inputMode="numeric" value={f.warn} onChange={(e) => setF((s) => ({ ...s, warn: e.target.value }))} />
        </Field>
        <Field label="Duración máxima de una llamada de voz (minutos)" hint="Aunque le sobren minutos, la llamada se corta aquí. Es un freno ante una llamada que se queda abierta.">
          <TextInput inputMode="numeric" value={f.max} onChange={(e) => setF((s) => ({ ...s, max: e.target.value }))} />
        </Field>
        {error && <p role="alert" className="text-sm text-signal">{error}</p>}
      </div>
    </Modal>
  );
}
